/**
 * Holds back an autoplaying video until it can actually be seen.
 *
 * The video inside is rendered without `autoplay` and with `preload="none"`, so the browser fetches
 * nothing for it up front. It's switched to autoplay and started once it's on the page and:
 *
 * - `data-start="near"` (the default): within a screen's height of the viewport. Used by Theme
 *   settings → Media loading → "Lazy-load media below the first section".
 * - `data-start="shown"`: displayed at all, wherever it is. Used for media the visitor lands on.
 *
 * Either way, a video that CSS hides (`display: none`) is never started, so where a section has a
 * desktop and a mobile video, only the one for the visitor's screen downloads. Resizing across the
 * breakpoint shows the other one, which starts then.
 *
 * The element is `display: contents` (base.css), so the video lays out as the direct child of
 * whatever wraps it. That also leaves it with no box to observe, so the video itself is observed.
 */

/** @param {HTMLVideoElement} video */
const start = (video) => {
  // A video whose sources are scoped to the other device found none to pick when it was parsed.
  if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) video.load();
  video.preload = 'auto';
  video.autoplay = true;
  void video.play().catch(() => null);
};

/**
 * Keeps device-scoped videos (`data-device-sources`, see snippets/hero-video.liquid) right when the
 * screen is resized across 750px. The browser picks a video's source once, when it's parsed, so the
 * video for the other device has none: once it's shown, it's loaded (and played, if it autoplays).
 * The one that was just hidden is paused rather than left decoding off screen. A video still waiting
 * in a <lazy-video> isn't touched; it's loaded when it's started.
 */
const desktop = window.matchMedia('(min-width: 750px)');
desktop.addEventListener('change', () => {
  for (const video of document.querySelectorAll('video[data-device-sources]')) {
    if (!(video instanceof HTMLVideoElement)) continue;

    const shown = video.checkVisibility ? video.checkVisibility() : video.getClientRects().length > 0;
    if (!shown) {
      if (!video.paused) video.pause();
      continue;
    }

    const waiting = video.closest('lazy-video') && !video.autoplay;
    if (waiting) continue;

    if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) video.load();
    if (video.autoplay && video.paused) void video.play().catch(() => null);
  }
});

/** @param {string} rootMargin */
const createObserver = (rootMargin) =>
  new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        observer.unobserve(entry.target);
        start(/** @type {HTMLVideoElement} */ (entry.target));
      }
    },
    { rootMargin }
  );

/**
 * A hidden element never intersects anything, so a margin wide enough to take in the whole page
 * turns "intersects" into "is displayed".
 */
const observers = {
  near: createObserver('100% 0px'),
  shown: createObserver('100000px'),
};

class LazyVideo extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {MutationObserver | null} */
  #childObserver = null;

  get #observer() {
    return this.dataset.start === 'shown' ? observers.shown : observers.near;
  }

  connectedCallback() {
    this.#watchVideo();

    // Defined before the page has been parsed, the element connects as soon as its start tag is
    // read, before the video inside it exists. Wait for the video to arrive.
    if (!this.#video) {
      this.#childObserver = new MutationObserver(() => this.#watchVideo());
      this.#childObserver.observe(this, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#childObserver?.disconnect();
    this.#childObserver = null;
    if (this.#video) {
      observers.near.unobserve(this.#video);
      observers.shown.unobserve(this.#video);
    }
    this.#video = null;
  }

  #watchVideo() {
    if (this.#video) return;

    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#childObserver?.disconnect();
    this.#childObserver = null;
    this.#observer.observe(this.#video);
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo);
}
